import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, imgUrl } from "@/lib/api";

export default function OrderSuccess() {
    const { orderId } = useParams();
    const { t } = useTranslation();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setOrder(false));
    }, [orderId]);

    if (order === null) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-neutral-500">{t("common.loading")}</div>;
    if (order === false) return <div className="max-w-[1400px] mx-auto px-6 py-24">{t("product.notFound")}</div>;

    return (
        <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24" data-testid="order-success-page">
            <div className="text-center mb-10 sm:mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f8f8f8]/20 border border-[#00aeef] rounded-full mb-6 nx-glow">
                    <Check className="w-8 h-8 text-[#0077b6]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("order.confirmedEyebrow")}</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter">
                    {t("order.title")}
                </h1>
                <p className="text-neutral-600 mt-4 text-sm sm:text-base break-all">
                    {t("order.reference")}: <span className="font-mono text-[#0077b6]" data-testid="order-id">{order.id}</span>
                </p>
            </div>

            <div className="border border-neutral-200 p-5 sm:p-6 lg:p-10 space-y-6 sm:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">{t("order.shippingTo")}</div>
                        <div className="text-neutral-800">
                            {order.shipping.full_name}
                            <br />
                            {order.shipping.address}
                            <br />
                            {order.shipping.city}, {order.shipping.state} {order.shipping.zip}
                            <br />
                            {order.shipping.country}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-2">{t("order.payment")}</div>
                        <div className="font-mono text-neutral-800">
                            {order.payment_summary.brand} ····{order.payment_summary.last4}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-4 mb-2">{t("order.status")}</div>
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0077b6]">
                            <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full nx-pulse" /> {order.status}
                        </div>
                    </div>
                </div>

                <div className="border-t border-neutral-200 pt-6 space-y-3">
                    {order.items.map((it, idx) => (
                        <div key={`${it.product_id}-${it.dose || idx}`} className="flex items-center gap-4 text-sm">
                            <div className="w-14 h-14 bg-white border border-neutral-200 overflow-hidden rounded-lg">
                                <img src={imgUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                                <div className="font-display font-semibold flex items-center gap-2 flex-wrap">
                                    {it.name}
                                    {it.dose && (
                                        <span className="font-mono text-[#0077b6] border border-[#00aeef]/30 px-1.5 py-0.5 rounded text-[10px]">{it.dose}</span>
                                    )}
                                </div>
                                <div className="text-xs text-neutral-500">{t("checkout.qty", { n: it.quantity })} · £{it.price.toFixed(2)} {t("order.each")}</div>
                            </div>
                            <div className="font-mono text-sm">£{it.line_total.toFixed(2)}</div>
                        </div>
                    ))}
                </div>

                <div className="border-t border-neutral-200 pt-6 space-y-2 text-sm">
                    <Row label={t("order.subtotal")} value={`£${order.subtotal.toFixed(2)}`} />
                    <Row label={t("order.shipping")} value={order.shipping_cost === 0 ? t("order.shippingFree") : `£${order.shipping_cost.toFixed(2)}`} />
                    <Row label={t("order.tax")} value={`£${order.tax.toFixed(2)}`} />
                    <div className="border-t border-neutral-200 pt-4 flex items-baseline justify-between">
                        <div className="text-xs uppercase tracking-[0.22em]">{t("order.totalPaid")}</div>
                        <div className="font-display text-3xl font-extrabold text-[#0077b6]">£{order.total.toFixed(2)}</div>
                    </div>
                </div>

                <div className="border border-[#00aeef]/30 bg-[#f8f8f8]/5 p-5 flex items-start gap-4">
                    <Package className="w-5 h-5 text-[#0077b6] flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display font-bold text-sm">{t("order.coldChainTitle")}</div>
                        <div className="text-xs text-neutral-600 mt-1 leading-relaxed">
                            {t("order.coldChainBody")}
                        </div>
                    </div>
                </div>
            </div>

            <div className="text-center mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to="/shop" className="border border-neutral-200 hover:border-[#0089c8] hover:text-black rounded-lg px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold">
                    {t("order.continueBrowsing")}
                </Link>
                <Link to="/account" className="bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white px-6 py-3 text-xs uppercase tracking-[0.18em] font-bold">
                    {t("order.viewMyOrders")}
                </Link>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <div className="text-neutral-500">{label}</div>
            <div className="font-mono text-neutral-800">{value}</div>
        </div>
    );
}
